import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Invoice, InvoiceLineItem } from '@/types/invoice.types';

export async function generateInvoicePDF(invoice: Invoice): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  
  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency,
    }).format(amount);
  };

  // Set font
  doc.setFont('helvetica');
  
  // Header
  doc.setFontSize(24);
  doc.setTextColor(51, 51, 51);
  doc.text('INVOICE', margin, 30);
  
  // Invoice number
  doc.setFontSize(12);
  doc.setTextColor(102, 102, 102);
  doc.text(invoice.invoice_number, margin, 40);
  
  // Dates and status (right aligned)
  doc.setFontSize(10);
  const rightX = pageWidth - margin;
  let rightY = 30;
  
  doc.text(`Issue Date: ${format(new Date(invoice.issue_date), 'MMM d, yyyy')}`, rightX, rightY, { align: 'right' });
  rightY += 7;
  doc.text(`Due Date: ${format(new Date(invoice.due_date), 'MMM d, yyyy')}`, rightX, rightY, { align: 'right' });
  rightY += 7;
  doc.text(`Status: ${invoice.status.toUpperCase()}`, rightX, rightY, { align: 'right' });
  
  // Bill To section
  let currentY = 60;
  doc.setFontSize(14);
  doc.setTextColor(51, 51, 51);
  doc.text('Bill To', margin, currentY);
  
  currentY += 10;
  doc.setFontSize(10);
  doc.setTextColor(51, 51, 51);
  
  if (invoice.bill_to_name) {
    doc.text(invoice.bill_to_name, margin, currentY);
    currentY += 6;
  }
  
  if (invoice.organization?.name) {
    doc.text(invoice.organization.name, margin, currentY);
    currentY += 6;
  }
  
  if (invoice.bill_to_address) {
    const addressLines = doc.splitTextToSize(invoice.bill_to_address, contentWidth * 0.6);
    addressLines.forEach((line: string) => {
      doc.text(line, margin, currentY);
      currentY += 6;
    });
  }
  
  if (invoice.bill_to_email) {
    doc.text(invoice.bill_to_email, margin, currentY);
    currentY += 6;
  }
  
  // Add some space before the table
  currentY += 10;
  
  // Line items table
  if (invoice.line_items && invoice.line_items.length > 0) {
    const tableData = invoice.line_items.map(item => [
      item.description,
      item.quantity.toString(),
      formatCurrency(item.unit_price),
      formatCurrency(item.amount)
    ]);
    
    autoTable(doc, {
      startY: currentY,
      head: [['Description', 'Quantity', 'Unit Price', 'Amount']],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [51, 51, 51],
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: 4,
        lineWidth: 0,
        lineColor: [51, 51, 51],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [51, 51, 51],
        fontSize: 10,
        cellPadding: 4,
      },
      columnStyles: {
        0: { cellWidth: 'auto' }, // Description
        1: { cellWidth: 30, halign: 'right' }, // Quantity
        2: { cellWidth: 40, halign: 'right' }, // Unit Price
        3: { cellWidth: 40, halign: 'right' }, // Amount
      },
      didDrawPage: function(data) {
        // Add bottom border to header
        doc.setDrawColor(51, 51, 51);
        doc.setLineWidth(0.5);
        doc.line(
          data.settings.margin.left,
          data.table.head[0].y + data.table.head[0].height,
          data.settings.margin.left + data.table.width,
          data.table.head[0].y + data.table.head[0].height
        );
      },
      margin: { left: margin, right: margin },
    });
    
    // Get the Y position after the table
    const finalY = (doc as any).lastAutoTable.finalY || currentY;
    currentY = finalY + 15;
  }
  
  // Totals section
  const totalsX = pageWidth - margin - 80;
  const labelX = totalsX - 40;
  
  doc.setFontSize(10);
  
  // Subtotal
  doc.text('Subtotal:', labelX, currentY, { align: 'right' });
  doc.text(formatCurrency(invoice.subtotal), totalsX + 80, currentY, { align: 'right' });
  currentY += 8;
  
  // Discount
  if (invoice.discount_amount > 0) {
    const discountLabel = `Discount (${((invoice.discount_rate || 0) * 100).toFixed(1)}%):`;
    doc.text(discountLabel, labelX, currentY, { align: 'right' });
    doc.text(`-${formatCurrency(invoice.discount_amount)}`, totalsX + 80, currentY, { align: 'right' });
    currentY += 8;
  }
  
  // Tax
  if (invoice.tax_amount > 0) {
    const taxLabel = `Tax (${((invoice.tax_rate || 0) * 100).toFixed(1)}%):`;
    doc.text(taxLabel, labelX, currentY, { align: 'right' });
    doc.text(formatCurrency(invoice.tax_amount), totalsX + 80, currentY, { align: 'right' });
    currentY += 8;
  }
  
  // Total (with emphasis)
  currentY += 5;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Total:', labelX, currentY, { align: 'right' });
  doc.text(formatCurrency(invoice.total_amount), totalsX + 80, currentY, { align: 'right' });
  
  // Notes section
  if (invoice.notes) {
    currentY += 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Notes', margin, currentY);
    
    currentY += 8;
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(invoice.notes, contentWidth);
    noteLines.forEach((line: string) => {
      doc.text(line, margin, currentY);
      currentY += 6;
    });
  }
  
  // Payment terms
  if (invoice.payment_terms) {
    currentY += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Payment Terms', margin, currentY);
    
    currentY += 8;
    doc.setFont('helvetica', 'normal');
    const termLines = doc.splitTextToSize(invoice.payment_terms, contentWidth);
    termLines.forEach((line: string) => {
      doc.text(line, margin, currentY);
      currentY += 6;
    });
  }
  
  // Footer
  const footerY = doc.internal.pageSize.height - 20;
  doc.setFontSize(9);
  doc.setTextColor(102, 102, 102);
  doc.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });
  
  // Convert to blob
  const pdfOutput = doc.output('blob');
  return pdfOutput;
}

export async function downloadInvoicePDF(invoice: Invoice) {
  const blob = await generateInvoicePDF(invoice);
  
  // Create download link
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${invoice.invoice_number}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Keep the HTML generation function for preview purposes
export function createInvoiceHTML(invoice: Invoice): string {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: invoice.currency,
    }).format(amount);
  };

  const lineItemsHTML = invoice.line_items?.map(item => `
    <tr>
      <td style="padding: 8px; border-bottom: 1px solid #eee;">${item.description}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${item.quantity}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.unit_price)}</td>
      <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.amount)}</td>
    </tr>
  `).join('') || '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Invoice ${invoice.invoice_number}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 40px;
        }
        .invoice-title {
          font-size: 32px;
          font-weight: bold;
          color: #333;
        }
        .invoice-number {
          font-size: 18px;
          color: #666;
        }
        .section {
          margin-bottom: 30px;
        }
        .section-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 8px;
          border-bottom: 2px solid #333;
          font-weight: bold;
        }
        .totals {
          margin-top: 20px;
          text-align: right;
        }
        .total-row {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 5px;
        }
        .total-label {
          margin-right: 20px;
        }
        .total-amount {
          font-weight: bold;
        }
        .footer {
          margin-top: 60px;
          padding-top: 20px;
          border-top: 1px solid #eee;
          text-align: center;
          color: #666;
          font-size: 12px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div class="invoice-title">INVOICE</div>
          <div class="invoice-number">${invoice.invoice_number}</div>
        </div>
        <div style="text-align: right;">
          <div><strong>Issue Date:</strong> ${format(new Date(invoice.issue_date), 'MMM d, yyyy')}</div>
          <div><strong>Due Date:</strong> ${format(new Date(invoice.due_date), 'MMM d, yyyy')}</div>
          <div><strong>Status:</strong> ${invoice.status.toUpperCase()}</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Bill To</div>
        <div>${invoice.bill_to_name || invoice.organization?.name || 'N/A'}</div>
        ${invoice.bill_to_address ? `<div>${invoice.bill_to_address}</div>` : ''}
        ${invoice.bill_to_email ? `<div>${invoice.bill_to_email}</div>` : ''}
      </div>

      <div class="section">
        <table>
          <thead>
            <tr>
              <th style="width: 50%;">Description</th>
              <th style="text-align: right;">Quantity</th>
              <th style="text-align: right;">Unit Price</th>
              <th style="text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemsHTML}
          </tbody>
        </table>
      </div>

      <div class="totals">
        <div class="total-row">
          <span class="total-label">Subtotal:</span>
          <span>${formatCurrency(invoice.subtotal)}</span>
        </div>
        ${invoice.discount_amount > 0 ? `
          <div class="total-row">
            <span class="total-label">Discount (${((invoice.discount_rate || 0) * 100).toFixed(1)}%):</span>
            <span>-${formatCurrency(invoice.discount_amount)}</span>
          </div>
        ` : ''}
        ${invoice.tax_amount > 0 ? `
          <div class="total-row">
            <span class="total-label">Tax (${((invoice.tax_rate || 0) * 100).toFixed(1)}%):</span>
            <span>${formatCurrency(invoice.tax_amount)}</span>
          </div>
        ` : ''}
        <div class="total-row" style="font-size: 20px; margin-top: 10px;">
          <span class="total-label total-amount">Total:</span>
          <span class="total-amount">${formatCurrency(invoice.total_amount)}</span>
        </div>
      </div>

      ${invoice.notes ? `
        <div class="section">
          <div class="section-title">Notes</div>
          <div>${invoice.notes}</div>
        </div>
      ` : ''}

      ${invoice.payment_terms ? `
        <div class="section">
          <div class="section-title">Payment Terms</div>
          <div>${invoice.payment_terms}</div>
        </div>
      ` : ''}

      <div class="footer">
        <p>Thank you for your business!</p>
      </div>
    </body>
    </html>
  `;
}